import React, { useState, useContext } from "react";
import {
  Box,
  Button,
  Container,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import IHorizontalStepperDataBody from "../../../../../../shared/contracts/IHorizontalStepperDataBody";
import DatasourceListComponent from "../datasource-list/datasource-list.component";
import { CREATE_REPORT_SECTION } from "../../../../../../shared/contracts/TCreateReportSection";
import AvailableColumnsListComponent from "../available-columns-list/available-columns-list.component";
import AppliedFiltersComponent from "../applied-filters/applied-filters.component";
import ReportMiscDetailsComponent from "../report-misc-details/report-misc-details.component";
import useReportingAndAnalyticsApi from "../../../useReportingAndAnalyticsApi";
import LoadingDialogComponent from "../../../../../../shared/components/loading-dialog/loading-dialog.component";
import useLoader from "../../../../../../shared/components/loading-dialog/useLoader.hook";
import { CreateReportFormContext } from "../../../../../../shared/context/create-report/create-report-context";
import CreateReportService, {
  IReportSaveData,
} from "../../create-report.component.service";
import useReportingUserApi from "../../../useReportingUserApi";
import IReportSaveDataContext from "../../../../../../shared/contracts/IReportSaveDataContext";

interface IProps {
  steps: IHorizontalStepperDataBody[];
  onApply: () => void;
}

export default function CreateReportStepperComponent({
  steps,
  onApply = () => {},
}: IProps) {
  const [activeStep, setActiveStep] = useState<IHorizontalStepperDataBody>(
    steps.find((x) => x.ordinal === 0)!
  );
  const [skipped, setSkipped] = useState(new Set<number>());
  const { saveReportDetails } = useReportingAndAnalyticsApi();
  const { createReportForm, dispatch } = useContext(CreateReportFormContext)!;
  const { userNameGlobally } = useReportingUserApi();

  function validateFields() {
    let valid = true;
    if (!createReportForm.reportName) {
      dispatch({
        type: "UPDATE_ERROR",
        payload: { field: "reportNameError", value: "Report Name is required" },
      });
      valid = false;
    }
    if (!createReportForm.category) {
      dispatch({
        type: "UPDATE_ERROR",
        payload: { field: "categoryError", value: "Category is required" },
      });
      valid = false;
    }
    if (!createReportForm.permissions) {
      dispatch({
        type: "UPDATE_ERROR",
        payload: { field: "permissionsError", value: "Rights are required" },
      });
      valid = false;
    }
    return valid;
  }

  const isStepOptional = (step: IHorizontalStepperDataBody) => step.isOptional;
  const isStepSkipped = (step: IHorizontalStepperDataBody) =>
    skipped.has(step.ordinal);

  const handleNext = () => {
    let isValid = true;
    if (
      activeStep.ordinal === CREATE_REPORT_SECTION.DATASOURCE &&
      !createReportForm.selectedDataSource
    ) {
      dispatch({
        type: "UPDATE_ERROR",
        payload: {
          field: "datasourceError",
          value: "Please select a datasource",
        },
      });
      isValid = false; // Halt progression to next step
    }
    if (activeStep.ordinal === CREATE_REPORT_SECTION.MISC) {
      isValid = validateFields();
    }
    if (!isValid) return;

    let newSkipped = skipped;
    if (isStepSkipped(activeStep)) {
      newSkipped = new Set(newSkipped.values());
      newSkipped.delete(activeStep.ordinal);
    }
    setActiveStep(
      (prevActiveStep) =>
        steps.find((x) => x.ordinal === prevActiveStep.ordinal + 1)!
    );
    setSkipped(newSkipped);
  };

  const handleBack = () => {
    setActiveStep(
      (prevActiveStep) =>
        steps.find((x) => x.ordinal === prevActiveStep.ordinal - 1)!
    );
  };

  const handleSkip = () => {
    if (!isStepOptional(activeStep)) {
      throw new Error("You can't skip a step that isn't optional.");
    }
    setActiveStep(
      (prevActiveStep) =>
        steps.find((x) => x.ordinal === prevActiveStep.ordinal + 1)!
    );
    setSkipped((prevSkipped) => {
      const newSkipped = new Set(prevSkipped.values());
      newSkipped.add(activeStep.ordinal);
      return newSkipped;
    });
  };

  const handleSubmit = async () => {
    console.log("Creating report with Form: ", createReportForm);
    const preparedData =
      CreateReportService.getSaveReportDetailsBody(createReportForm);

    console.log("Prepared Data for Submission:", preparedData);

    saveReportDetails(preparedData, {
      doIf: () => true,
      doBefore: () => console.log("Saving Report..."),
      onFulfilled: (data) => {
        console.log("Report saved successfully: ", data);
        onApply();
      },
      onException: (error) => {
        console.error("Error in saving report:", error);
      },
    });
  };

  return (
    <>
      <Box sx={{ width: "100%" }}>
        <Stepper activeStep={activeStep.ordinal}>
          {steps.map((step, index) => {
            const stepProps: { completed?: boolean } = {};
            const labelProps: { optional?: React.ReactNode } = {};
            if (isStepOptional(step)) {
              labelProps.optional = (
                <Typography variant="caption">Optional</Typography>
              );
            }
            if (isStepSkipped(step)) {
              stepProps.completed = false;
            }
            return (
              <Step key={step.ordinal} {...stepProps}>
                <StepLabel {...labelProps}>{step.label}</StepLabel>
              </Step>
            );
          })}
        </Stepper>
        <Container>
          {activeStep.ordinal === steps.length - 1 ? (
            <>
              <Typography
                sx={{ mt: 2, mb: 1, display: "flex", justifyContent: "center" }}
              >
                Clicking on 'Submit' will create a new report.
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
                <Button
                  color="inherit"
                  disabled={activeStep.ordinal === 0}
                  onClick={handleBack}
                  sx={{ mr: 1 }}
                >
                  Back
                </Button>
                <Box sx={{ flex: "1 1 auto" }} />
                <Button onClick={handleSubmit}>Submit</Button>
              </Box>
            </>
          ) : (
            <>
              <Box
                component={"form"}
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  minHeight: "calc(20vh)",
                  padding: "2%",
                }}
              >
                {activeStep.ordinal === CREATE_REPORT_SECTION.DATASOURCE && (
                  <DatasourceListComponent />
                )}
                {activeStep.ordinal === CREATE_REPORT_SECTION.FIELDS && (
                  <AvailableColumnsListComponent />
                )}
                {activeStep.ordinal === CREATE_REPORT_SECTION.FILTERS && (
                  <AppliedFiltersComponent />
                )}
                {activeStep.ordinal === CREATE_REPORT_SECTION.MISC && (
                  <ReportMiscDetailsComponent />
                )}
              </Box>
              <Box sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
                <Button
                  color="inherit"
                  disabled={activeStep.ordinal === 0}
                  onClick={handleBack}
                  sx={{ mr: 1 }}
                >
                  Back
                </Button>
                <Box sx={{ flex: "1 1 auto" }} />
                {isStepOptional(activeStep) && (
                  <Button color="inherit" onClick={handleSkip} sx={{ mr: 1 }}>
                    Skip
                  </Button>
                )}
                <Button onClick={handleNext}>
                  {activeStep.ordinal === steps.length - 1 ? "Finish" : "Next"}
                </Button>
              </Box>
            </>
          )}
        </Container>
        {/* {<LoadingDialogComponent loaderHook={saveReportLoader} />} */}
      </Box>
    </>
  );
}
